import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { appLocale, currentAppLang } from '../../pipes/vw-date.pipe';

@Component({
  selector: 'vw-date-picker',
  imports: [
    NgClass,
    FormsModule,
    TranslatePipe
  ],
  templateUrl: './date-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './date-picker.component.scss'
})
export class DatePickerComponent implements OnInit, OnChanges {
  MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  DAYS_NUM = ['01', '02', '03', '04', '05', '06', '07'];
  DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  @Input() showDatepicker = false;
  @Input() showInput = false;
  @Input() initialValue: Date;
  /** Dernier jour sélectionnable (un geste déjà fait : aujourd'hui). Sans valeur, aucune limite. */
  @Input() maxDate?: Date;
  @Output() dateSelected = new EventEmitter<Date>();
  datepickerValue!: string;
  month!: number; // !: mean promis it will not be null, and it will definitely be assigned
  year!: number;
  no_of_days = [] as number[];
  blankdays = [] as number[];


  constructor(private translateService: TranslateService) { }

  ngOnInit(): void {
    this.initDate();
    this.getNoOfDays();
  }

  /** Une date imposée par le parent (date rapide « Hier ») se montre au calendrier. */
  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['initialValue'];
    if (change && !change.firstChange && change.currentValue) {
      this.initDate();
      this.getNoOfDays();
    }
  }

  /** Mois précédent ou suivant, en passant d'une année à l'autre. */
  changeMonth(delta: number) {
    const target = new Date(this.year, this.month + delta, 1);
    this.month = target.getMonth();
    this.year = target.getFullYear();
    this.getNoOfDays();
  }

  /** Le mois suivant commence après la date maximale. */
  get isNextMonthDisabled(): boolean {
    return !!this.maxDate && new Date(this.year, this.month + 1, 1) > this.maxDate;
  }

  isDisabled(date: number): boolean {
    if (!this.maxDate) return false;
    const max = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate());
    return new Date(this.year, this.month, date) > max;
  }

  initDate() {
    let today = new Date();
    if (this.initialValue) {
      today = this.initialValue;
    }
    this.dateSelected.emit(today);
    this.month = today.getMonth();
    this.year = today.getFullYear();
    this.datepickerValue = this.formatDate(new Date(this.year, this.month, today.getDate()));
  }

  isToday(date: any) {
    const today = new Date();
    const d = new Date(this.year, this.month, date);
    return today.getDate() === d.getDate() &&
      today.getMonth() === d.getMonth() &&
      today.getFullYear() === d.getFullYear();
  }

  isSelectedDate(date: any) {
    const selectedDate = new Date(this.year, this.month, date);
    const formattedSelectedDate = this.formatDate(selectedDate);
    return formattedSelectedDate === this.datepickerValue;
  }

  getDateValue(date: any) {
    if (this.isDisabled(date)) return;
    let selectedDate = new Date(this.year, this.month, date);
    this.dateSelected.emit(selectedDate);
    this.datepickerValue = this.formatDate(selectedDate);
    this.showDatepicker = this.showInput ? false : true;
  }

  getNoOfDays() {
    const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();

    // find where to start calendar day of week
    let firstDayOfMonth = new Date(this.year, this.month, 1).getDay(); // 0 = Sunday, 6 = Saturday

    // Ajustement pour que Lundi = 0
    let adjustedFirstDay = (firstDayOfMonth === 0) ? 6 : firstDayOfMonth - 1;

    let blankdaysArray: number[] = [];
    for (let i = 0; i < adjustedFirstDay; i++) {
      blankdaysArray.push(i);
    }

    let daysArray: number[] = [];
    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push(i);
    }

    this.blankdays = blankdaysArray;
    this.no_of_days = daysArray;
  }


  trackByIdentity = (index: number, item: any) => item;

  /**
   * « 29 octobre 2026 » dans la langue de l'app : le mois en minuscule au
   * milieu d'une date (la clé `common.months` sert de titre au calendrier et
   * porte une majuscule : « 29 Octobre 2026 » sortait dans les champs).
   */
  private formatDate(date: Date): string {
    return new Intl.DateTimeFormat(appLocale(currentAppLang(this.translateService)), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }
}
